import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { forgotPassword } from '../../services/api';
import { applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const tenantSlug = searchParams.get('tenant');

  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState('');
  const [branding, setBranding] = useState(null); // { name, logo_url, primary_color }

  // Load tenant branding when ?tenant=slug is in the URL
  useEffect(() => {
    if (!tenantSlug) return;
    axios.get(`${API_URL}/public/${tenantSlug}`)
      .then(r => {
        const t = r.data.tenant;
        if (t) setBranding({ name: t.name, logo_url: t.logo_url, primary_color: t.primary_color || '#2563eb' });
      })
      .catch(() => {});
  }, [tenantSlug]);

  // Apply tenant branding (primary + dark variação gerada automaticamente)
  useEffect(() => {
    if (branding?.primary_color) {
      applyTenantBranding(branding.primary_color);
    }
    return () => clearTenantBranding();
  }, [branding]);

  const loginHref = `/login${tenantSlug ? `?tenant=${tenantSlug}` : ''}`;

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Informe seu e-mail'); return; }
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase(), tenantSlug || undefined);
      setSent(true);
    } catch (err) {
      // Even on backend error we show the same neutral message to avoid leaking info
      const msg = err.response?.data?.error;
      if (msg && err.response?.status < 500) {
        setError(msg);
      } else {
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          {branding ? (
            <>
              {branding.logo_url
                ? <img src={branding.logo_url} alt={branding.name} style={{ height:64, objectFit:'contain', marginBottom:10, borderRadius:8 }} />
                : <div style={{ width:64, height:64, background:'var(--primary)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'white', fontSize:26, margin:'0 auto 10px' }}>
                    {branding.name?.[0]?.toUpperCase() || 'A'}
                  </div>
              }
              <h1>{branding.name}</h1>
              <p>Recuperar acesso</p>
            </>
          ) : (
            <>
              <img src="/logo-full.svg" alt="AtendoApp" style={{ height:48, objectFit:'contain', marginBottom:10 }} />
              <p>Plataforma de agendamentos SaaS</p>
            </>
          )}
        </div>

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div className="success-box" style={{ marginBottom: 24, textAlign: 'left' }}>
              <div style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>✉️</div>
              <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 15 }}>Verifique seu e-mail</p>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
                Se o endereço <strong>{email}</strong> estiver cadastrado, você receberá um link para redefinir sua senha em breve.
              </p>
            </div>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 8 }}>
              Não recebeu o e-mail? Verifique sua pasta de spam ou aguarde alguns minutos.
            </p>
            <Link
              to={loginHref}
              style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            >
              ← Voltar ao login
            </Link>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 24, lineHeight: 1.6 }}>
              Informe o e-mail cadastrado na sua conta e enviaremos as instruções para redefinir sua senha.
            </p>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="error-box" style={{ marginBottom: 16 }}>⚠️ {error}</div>
              )}

              <div className="form-group">
                <label>E-mail</label>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
                style={{ width: '100%', padding: 11, fontSize: 14, marginTop: 4 }}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar instruções'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13 }}>
              <Link
                to={loginHref}
                style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
              >
                ← Voltar ao login
              </Link>
            </p>
          </>
        )}

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--gray-400)' }}>
          {branding ? `Desenvolvido por AtendoApp` : `AtendoApp © ${new Date().getFullYear()}`}
        </p>
      </div>
    </div>
  );
}
